import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math'
import { Scene } from '@babylonjs/core/scene'
import { Settings } from './Settings'

type fromTo = number | Vector3 | Quaternion | (number | Vector3 | Quaternion)[]

export default class AnimationTimer {
	target: any
	property: string

	start: number
	duration: number
	end: number
	now: number

	complete: Function

	from: fromTo
	to: fromTo
	properties: string[]

	scene: Scene

	constructor(scene: Scene, config: { target: any; property?: string; duration?: number; from?: fromTo; to?: fromTo; properties?: string[]; complete?: () => void }) {
		this.target = config.target
		this.property = config.property
		this.duration = config.duration ?? Settings.morphDuration // * 10

		this.from = config.from
		this.to = config.to
		this.properties = config.properties

		this.complete = config.complete
		this.scene = scene

		this.start = performance.now()
		this.end = performance.now() + this.duration * 1000

		// TODO: BABYLON.setAndStartTimer
		this.scene.registerBeforeRender(this.update = this.update.bind(this))
	}

	setProperty(progress: number, property: string, from: number | Vector3 | Quaternion, to: number | Vector3 | Quaternion) {
		if (typeof from == 'number' && typeof to == 'number') {
			this.target[property] = (progress * (to - from)) + from
		} else if (typeof from == 'object' && from.getClassName() == 'Vector3') {
			const vec = new Vector3(progress, progress, progress).multiply((to as Vector3).subtract(from as Vector3)).add(from as Vector3)
			this.target[property] = vec
		} else if (typeof from == 'object' && from.getClassName() == 'Quaternion') {
			const vec = new Quaternion(progress, progress, progress).multiply((to as Quaternion).subtract(from as Quaternion)).add(from as Quaternion)
			this.target[property] = vec
		}
	}

	update() {
		const prev = this.now
		this.now = performance.now()
		const progress = AnimationTimer.easeOutQuart(Math.min(1, (this.now - this.start) / (this.end - this.start)))
		// const progress = Math.min(1, (this.now-this.start) / (this.end-this.start))

		if (this.from || this.to) {
			if (this.property) {
				this.setProperty(progress, this.property, this.from as number | Vector3 | Quaternion, this.to as number | Vector3 | Quaternion)
			} else if (this.properties) {
				this.properties.forEach((property, i) => this.setProperty(progress, property, this.from[i], this.to[i]))
			}
		}

		if (this.now > this.end) {
			if (this.complete) this.complete()
			this.scene.unregisterBeforeRender(this.update)
		}
	}

	static linear = (t: number) => t

	static easeInQuad = (t: number) => t * t
	static easeOutQuad = (t: number) => t * (2 - t)
	static easeInOutQuad = (t: number) => t < .5 ? 2 * t * t : -1 + (4 - 2 * t) * t

	static easeInCubic = (t: number) => t * t * t
	static easeOutCubic = (t: number) => (--t) * t * t + 1
	static easeInOutCubic = (t: number) => t < .5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1

	static easeInQuart = (t: number) => t * t * t * t
	static easeOutQuart = (t: number) => 1 - (--t) * t * t * t
	static easeInOutQuart = (t: number) => t < .5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t

	static easeInQuint = (t: number) => t * t * t * t * t
	static easeOutQuint = (t: number) => 1 + (--t) * t * t * t * t
	static easeInOutQuint = (t: number) => t < .5 ? 16 * t * t * t * t * t : 1 + 16 * (--t) * t * t * t * t

	static easeInSine = (t: number) => 1 + Math.sin(Math.PI / 2 * t - Math.PI / 2)
	static easeOutSine = (t: number) => Math.sin(Math.PI / 2 * t)
	static easeInOutSine = (t: number) => (1 + Math.sin(Math.PI * t - Math.PI / 2)) / 2

	static easeInElastic = (t: number) => (.04 - .04 / t) * Math.sin(25 * t) + 1
	static easeOutElastic = (t: number) => .04 * t / (--t) * Math.sin(25 * t)
	static easeInOutElastic = (t: number) => (t -= .5) < 0 ? (.02 + .01 / t) * Math.sin(50 * t) : (.02 - .01 / t) * Math.sin(50 * t) + 1
}
